import { useApi } from '@hooks/useApi';
import { Blockquote } from '@ui';
import Dropdown from '@ui/Dropdown';
import SectionHeading from '@ui/SectionHeading';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MollieConnectPanel, PaymentModuleKey } from '../types';
import { accountName } from './AccountCard';

interface Props {
    clubSlug: string;
    panel: MollieConnectPanel;
    onChange: (assignments: MollieConnectPanel['assignments']) => void;
}

export default function ModuleAssignments({ clubSlug, panel, onChange }: Props) {
    const { t } = useTranslation('general');
    const api = useApi();
    const [saving, setSaving] = useState<PaymentModuleKey | null>(null);
    const assignments = panel.assignments ?? {};
    const defaultAccount = panel.accounts.find((account) => account.is_default) ?? panel.accounts[0];

    const assign = (module: PaymentModuleKey, accountId: number | null) => {
        if ((assignments[module] ?? null) === accountId) return;
        setSaving(module);
        api({
            url: `connect/${clubSlug}/assignments`,
            method: 'PUT',
            data: { assignments: { [module]: accountId } },
            onBefore: () => setSaving(null),
            onSuccess: (data) => onChange(data.assignments)
        });
    };

    return (
        <div className="tw-space-y-3">
            <SectionHeading
                title={t('mollie.assistant.assignmentsTitle')}
                description={t('mollie.assistant.assignmentsDescription')}
            />
            <div className="tw-space-y-2">
                {(panel.modules ?? []).map((module) => {
                    const assigned = panel.accounts.find((account) => account.id === assignments[module.key]);

                    return (
                        <div key={module.key} className="tw-flex tw-items-center tw-justify-between tw-gap-4">
                            <span className="tw-text-sm">{t(`mollie.modules.${module.key}`, module.label)}</span>
                            <Dropdown
                                size="sm"
                                color="light"
                                isLoading={saving === module.key}
                                label={
                                    assigned
                                        ? accountName(assigned)
                                        : t('mollie.assistant.useDefault', { name: accountName(defaultAccount) })
                                }
                            >
                                <Dropdown.Item
                                    label={t('mollie.assistant.useDefault', { name: accountName(defaultAccount) })}
                                    onClick={() => assign(module.key, null)}
                                />
                                {panel.accounts.map((account) => (
                                    <Dropdown.Item
                                        key={account.id}
                                        label={accountName(account)}
                                        onClick={() => assign(module.key, account.id)}
                                    />
                                ))}
                            </Dropdown>
                        </div>
                    );
                })}
            </div>
            <Blockquote>{t('mollie.assistant.mandateHint')}</Blockquote>
        </div>
    );
}
